import { useState, useEffect, useMemo, useId } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Badge, Button, Card, Alert } from 'flowbite-react';
import {
  HiOfficeBuilding,
  HiLocationMarker,
  HiPhone,
  HiMail,
  HiGlobe,
  HiUsers,
  HiAcademicCap,
  HiArrowLeft,
  HiPencil,
  HiUserGroup,
  HiPlus,
  HiTrash,
  HiCalendar,
  HiChartBar
} from 'react-icons/hi';
import { leasApi, contactsApi, getUserErrorMessage, type ContactSearchResult } from '../api';
import { descriptorsApi, type Descriptor } from '../api/descriptors';
import { Loading } from '../components/common/Loading';
import { SearchableMultiSelect, type MultiSelectOption } from '../components/common/SearchableMultiSelect';
import { EmbeddedDistrictMap } from '../components/map';
import { PageHeader } from '../components/common/PageHeader';
import { IconButton } from '../components/common/IconButton';
import { useOrganizationPermissions } from '../hooks/useOrganizationPermissions';
import { useAuth } from '../contexts/AuthContext';
import type { LEAResponse } from '../types/api';

// Tab panels
import { SchoolsTab, StaffTab, BoardTab, AIUDirectoryTab, CalendarTab, ActivityTab, OrganizationLogo, OrganizationLogoEditor } from '../components/organization';
import { useAppSettings, APP_SETTING_KEYS } from '../contexts/AppSettingsContext';
import { EditAddressModal, EditPhoneModal, EditOrgEmailModal } from '../components/modals';
import type { AddressResponse, PhoneResponse, IdentificationCodeResponse, OrgEmailAddressResponse } from '../types/api';

// Inline fields in the Overview card: full width, and 16px text on phones so
// iOS doesn't zoom in on focus
const FIELD_CLASS = 'block w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-base text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/40 sm:text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white dark:placeholder-gray-500';
const FIELD_LABEL_CLASS = 'mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300';
const SAVE_BUTTON_CLASS = 'rounded-lg bg-blue-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50 dark:focus-visible:ring-offset-gray-800';
const breakableEmail = (email: string) => {
  const at = email.indexOf('@');
  return at > 0 ? <>{email.slice(0, at)}<wbr />{email.slice(at)}</> : email;
};
const CANCEL_BUTTON_CLASS = 'rounded-lg bg-gray-200 px-3 py-1.5 text-sm font-medium text-gray-800 hover:bg-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 dark:bg-gray-600 dark:text-gray-100 dark:hover:bg-gray-500';

export const OrganizationProfilePage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isSuperAdmin } = useAuth();
  const { getBool } = useAppSettings();
  const calendarTabEnabled = getBool(APP_SETTING_KEYS.calendarTabEnabled, false);
  const [lea, setLea] = useState<LEAResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Active tab is mirrored to the URL (?tab=staff) so the browser back
  // button restores the tab the user had open when they left the page.
  // We use replaceState so flipping between tabs doesn't add history entries.
  const validTabs = useMemo(() => {
    const tabs = ['schools', 'staff', 'board'];
    if (lea?.nameOfInstitution === import.meta.env.VITE_AIU_ORGANIZATION_NAME) {
      tabs.push('aiu-directory');
    }
    if (calendarTabEnabled) {
      tabs.push('calendar');
    }
    if (isSuperAdmin) {
      tabs.push('activity');
    }
    return tabs;
  }, [lea?.nameOfInstitution, calendarTabEnabled, isSuperAdmin]);
  const tabParam = searchParams.get('tab');
  const activeTab = tabParam && validTabs.includes(tabParam) ? tabParam : 'schools';
  const setActiveTab = (tabId: string) => {
    const next = new URLSearchParams(searchParams);
    if (tabId === 'schools') {
      next.delete('tab');
    } else {
      next.set('tab', tabId);
    }
    setSearchParams(next, { replace: true });
  };
  const [isEditMode, setIsEditMode] = useState(false);
  const [showInactive, setShowInactive] = useState(false);
  // Separate from showInactive: closed schools and ended staff assignments
  // are different concerns. An open school can have inactive staff; a closed
  // school is its own surface decision. Gated to the SchoolsTab UI.
  const [showClosedSchools, setShowClosedSchools] = useState(false);
  const [mapRefreshKey, setMapRefreshKey] = useState(0);

  // Leadership data (fetched from contacts API by role tag)
  const [superintendent, setSuperintendent] = useState<ContactSearchResult | null>(null);
  const [assistantSuperintendents, setAssistantSuperintendents] = useState<ContactSearchResult[]>([]);

  // Permission checks
  const { canEditOrganization, canAddSchools } = useOrganizationPermissions();
  const canEdit = lea ? canEditOrganization(lea.educationOrganizationId) : false;

  // Modal state
  const [addressModalOpen, setAddressModalOpen] = useState(false);
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<AddressResponse | null>(null);
  const [editingPhone, setEditingPhone] = useState<PhoneResponse | null>(null);

  // Org email modal state
  const [emailModalOpen, setEmailModalOpen] = useState(false);
  const [editingEmail, setEditingEmail] = useState<OrgEmailAddressResponse | null>(null);
  const [showAllEmails, setShowAllEmails] = useState(false);

  // Inline editing state
  const [editingWebsite, setEditingWebsite] = useState(false);
  const [websiteValue, setWebsiteValue] = useState('');
  const [websiteError, setWebsiteError] = useState<string | null>(null);
  const [editingValidDomains, setEditingValidDomains] = useState(false);
  const [validDomainsValue, setValidDomainsValue] = useState('');
  const [validDomainsError, setValidDomainsError] = useState<string | null>(null);
  // Ties each inline field's label to its input
  const fieldId = useId();

  // Identification code management state (superAdmin only)
  const [showIdCodeManager, setShowIdCodeManager] = useState(false);
  const [idCodeDescriptors, setIdCodeDescriptors] = useState<Descriptor[]>([]);
  const [idCodeFormOpen, setIdCodeFormOpen] = useState(false);
  const [editingIdCode, setEditingIdCode] = useState<IdentificationCodeResponse | null>(null);
  const [idCodeForm, setIdCodeForm] = useState({ identificationCode: '', descriptorId: 0 });
  const [savingIdCode, setSavingIdCode] = useState(false);
  const [idCodeError, setIdCodeError] = useState<string | null>(null);

  // Districts served name resolution and edit state (for CTCs/Jointures)
  const [servedDistrictNames, setServedDistrictNames] = useState<Array<{ id: number; leaId: number; name: string }>>([]);
  const [allOrgOptions, setAllOrgOptions] = useState<MultiSelectOption[]>([]);
  const [editingDistrictsServed, setEditingDistrictsServed] = useState<number[]>([]);
  const [savingDistrictsServed, setSavingDistrictsServed] = useState(false);

  useEffect(() => {
    // Load all LEA options (needed for both name resolution and edit picker)
    const loadOrgOptions = async () => {
      try {
        const allLeas = await leasApi.list({ limit: 500 });
        setAllOrgOptions(
          allLeas.map(l => ({
            id: l.educationOrganizationId,
            label: l.leaType
              ? `${l.nameOfInstitution} [${l.leaType}]`
              : l.nameOfInstitution,
          }))
        );

        // Resolve served district names
        if (lea?.districtsServed && lea.districtsServed.length > 0) {
          const matched = lea.districtsServed
            .map(edOrgId => {
              const found = allLeas.find(l => l.educationOrganizationId === edOrgId);
              return found ? { id: edOrgId, leaId: found.leaId, name: found.nameOfInstitution } : null;
            })
            .filter(Boolean) as Array<{ id: number; leaId: number; name: string }>;
          setServedDistrictNames(matched);
        } else {
          setServedDistrictNames([]);
        }
      } catch {
        // Silent - non-critical
      }
    };
    loadOrgOptions();
  }, [lea?.districtsServed]);

  // Sync editing state when edit mode activates or lea changes
  useEffect(() => {
    if (isEditMode && lea?.districtsServed) {
      setEditingDistrictsServed(lea.districtsServed);
    }
  }, [isEditMode, lea?.districtsServed]);

  useEffect(() => {
    if (id) {
      loadOrganization(parseInt(id));
    }
  }, [id, showInactive, showClosedSchools]);

  const handleSaveDistrictsServed = async () => {
    if (!lea) return;
    setSavingDistrictsServed(true);
    try {
      await leasApi.update(lea.leaId, {
        districtsServed: editingDistrictsServed.length > 0 ? editingDistrictsServed : null,
      });
      await loadOrganization(lea.leaId, true);
    } catch (err: any) {
      setError(getUserErrorMessage(err, 'Failed to update districts served'));
    } finally {
      setSavingDistrictsServed(false);
    }
  };

  // Load identification code system descriptors when manager opens
  useEffect(() => {
    if (showIdCodeManager && idCodeDescriptors.length === 0) {
      descriptorsApi.getByTypeName('Education Organization Identification System')
        .then(setIdCodeDescriptors)
        .catch(() => console.warn('Failed to load identification system descriptors'));
    }
  }, [showIdCodeManager]);

  const handleOpenIdCodeForm = (code?: IdentificationCodeResponse) => {
    if (code) {
      setEditingIdCode(code);
      setIdCodeForm({
        identificationCode: code.identificationCode,
        descriptorId: code.educationOrganizationIdentificationSystemDescriptor || 0,
      });
    } else {
      setEditingIdCode(null);
      setIdCodeForm({ identificationCode: '', descriptorId: 0 });
    }
    setIdCodeFormOpen(true);
    setIdCodeError(null);
  };

  const handleSaveIdCode = async () => {
    if (!lea || !idCodeForm.identificationCode.trim()) return;
    setSavingIdCode(true);
    setIdCodeError(null);
    try {
      if (editingIdCode) {
        await leasApi.updateIdentificationCode(
          lea.leaId,
          editingIdCode.educationOrganizationIdentificationCodeId,
          {
            identificationCode: idCodeForm.identificationCode.trim(),
            educationOrganizationIdentificationSystemDescriptor: idCodeForm.descriptorId || null,
          }
        );
      } else {
        await leasApi.addIdentificationCode(lea.leaId, {
          identificationCode: idCodeForm.identificationCode.trim(),
          educationOrganizationIdentificationSystemDescriptor: idCodeForm.descriptorId || null,
        });
      }
      setEditingIdCode(null);
      setIdCodeFormOpen(false);
      setIdCodeForm({ identificationCode: '', descriptorId: 0 });
      await loadOrganization(lea.leaId, true);
    } catch (err: any) {
      setIdCodeError(getUserErrorMessage(err, 'Failed to save identification code'));
    } finally {
      setSavingIdCode(false);
    }
  };

  const handleDeleteIdCode = async (codeId: number) => {
    if (!lea || !confirm('Delete this identification code?')) return;
    try {
      await leasApi.removeIdentificationCode(lea.leaId, codeId);
      await loadOrganization(lea.leaId, true);
    } catch (err: any) {
      setIdCodeError(getUserErrorMessage(err, 'Failed to delete identification code'));
    }
  };

  const loadOrganization = async (leaId: number, isRefresh: boolean = false) => {
    try {
      // Only show full loading state for initial load, not for background refreshes
      if (!isRefresh) {
        setLoading(true);
      }
      setError(null);
      const data = await leasApi.get(leaId, {
        includeStaff: true,
        includeSchools: true,
        includeInactive: showInactive,
        includeClosedSchools: showClosedSchools,
        bustCache: isRefresh  // Prevent browser caching on refresh
      });
      setLea(data);
      if (isRefresh) {
        setMapRefreshKey(prev => prev + 1);
      }

      // Fetch leadership data (superintendent, assistant superintendents) from contacts API
      // Use the educationOrganizationId to filter by organization
      try {
        const [suprResponse, asstResponse] = await Promise.all([
          contactsApi.search({
            organizationId: data.educationOrganizationId,
            roleTag: 'Superintendent',
            includeInDirectoryOnly: false, // Include role tags that aren't in directory
            limit: 1
          }),
          contactsApi.search({
            organizationId: data.educationOrganizationId,
            roleTag: 'AssistantSuperintendent',
            includeInDirectoryOnly: false,
            limit: 10
          })
        ]);

        // Set superintendent (first result if any)
        setSuperintendent(suprResponse.results.length > 0 ? suprResponse.results[0] : null);
        setAssistantSuperintendents(asstResponse.results);
      } catch (leadershipErr) {
        // Don't fail the whole page if leadership fetch fails
        console.error('Failed to load leadership data:', leadershipErr);
        setSuperintendent(null);
        setAssistantSuperintendents([]);
      }
    } catch (err: any) {
      setError(getUserErrorMessage(err, 'Failed to load organization details'));
      console.error('Failed to load LEA:', err);
    } finally {
      if (!isRefresh) {
        setLoading(false);
      }
    }
  };

  // Address handlers
  const handleEditAddress = (address: AddressResponse) => {
    setEditingAddress(address);
    setAddressModalOpen(true);
  };

  const handleAddAddress = () => {
    setEditingAddress(null);
    setAddressModalOpen(true);
  };

  const handleSaveAddress = async (addressData: Partial<AddressResponse>) => {
    if (!lea) return;
    try {
      if (editingAddress) {
        await leasApi.updateAddress(lea.leaId, editingAddress.educationOrganizationAddressId, addressData);
      } else {
        await leasApi.addAddress(lea.leaId, addressData);
      }
      setAddressModalOpen(false);
      await loadOrganization(lea.leaId, true);
    } catch (err: any) {
      throw err;
    }
  };

  const handleDeleteAddress = async (addressId: number) => {
    if (!lea) return;
    if (!confirm('Are you sure you want to delete this address?')) return;
    try {
      await leasApi.removeAddress(lea.leaId, addressId);
      await loadOrganization(lea.leaId, true);
    } catch (err: any) {
      console.error('Failed to delete address:', err);
    }
  };

  // Phone handlers
  const handleEditPhone = (phone: PhoneResponse) => {
    setEditingPhone(phone);
    setPhoneModalOpen(true);
  };

  const handleAddPhone = () => {
    setEditingPhone(null);
    setPhoneModalOpen(true);
  };

  const handleSavePhone = async (phoneData: Partial<PhoneResponse>) => {
    if (!lea) return;
    try {
      if (editingPhone) {
        await leasApi.updatePhone(lea.leaId, editingPhone.educationOrganizationPhoneAssociationId, phoneData);
      } else {
        await leasApi.addPhone(lea.leaId, phoneData);
      }
      setPhoneModalOpen(false);
      await loadOrganization(lea.leaId, true);
    } catch (err: any) {
      throw err;
    }
  };

  const handleDeletePhone = async (phoneId: number) => {
    if (!lea) return;
    if (!confirm('Are you sure you want to delete this phone number?')) return;
    try {
      await leasApi.removePhone(lea.leaId, phoneId);
      await loadOrganization(lea.leaId, true);
    } catch (err: any) {
      console.error('Failed to delete phone:', err);
    }
  };

  // Org email handlers
  const handleEditEmail = (email: OrgEmailAddressResponse) => {
    setEditingEmail(email);
    setEmailModalOpen(true);
  };

  const handleAddEmail = () => {
    setEditingEmail(null);
    setEmailModalOpen(true);
  };

  const handleSaveEmail = async (emailData: {
    emailAddress: string;
    emailTypeDescriptorId?: number | null;
    primaryEmailIndicator: boolean;
  }) => {
    if (!lea) return;
    try {
      if (editingEmail) {
        await leasApi.updateEmail(lea.leaId, editingEmail.educationOrganizationEmailAddressId, emailData);
      } else {
        await leasApi.addEmail(lea.leaId, emailData);
      }
      setEmailModalOpen(false);
      await loadOrganization(lea.leaId, true);
    } catch (err: any) {
      throw err;
    }
  };

  const handleDeleteEmail = async (emailId: number) => {
    if (!lea) return;
    if (!confirm('Are you sure you want to delete this email address?')) return;
    try {
      await leasApi.removeEmail(lea.leaId, emailId);
      await loadOrganization(lea.leaId, true);
    } catch (err: any) {
      console.error('Failed to delete email:', err);
    }
  };

  // Website handlers
  const handleEditWebsite = () => {
    setWebsiteValue(lea?.webSite || '');
    setWebsiteError(null);
    setEditingWebsite(true);
  };

  const handleSaveWebsite = async () => {
    if (!lea) return;
    try {
      const updateData = { webSite: websiteValue };
      console.log('Updating website with:', updateData, 'for LEA:', lea.leaId);
      await leasApi.update(lea.leaId, updateData);
      setEditingWebsite(false);
      await loadOrganization(lea.leaId, true);
    } catch (err: any) {
      console.error('Failed to update website:', err);
      setWebsiteError(getUserErrorMessage(err, 'Failed to update website'));
    }
  };

  const handleCancelWebsite = () => {
    setEditingWebsite(false);
    setWebsiteValue('');
    setWebsiteError(null);
  };

  // Valid domains handlers (superAdmin only)
  const handleEditValidDomains = () => {
    setValidDomainsValue(lea?.validDomains || '');
    setValidDomainsError(null);
    setEditingValidDomains(true);
  };

  const handleSaveValidDomains = async () => {
    if (!lea) return;
    try {
      await leasApi.update(lea.leaId, { validDomains: validDomainsValue || null });
      setEditingValidDomains(false);
      await loadOrganization(lea.leaId, true);
    } catch (err: any) {
      console.error('Failed to update valid domains:', err);
      setValidDomainsError(getUserErrorMessage(err, 'Failed to update valid domains'));
    }
  };

  const handleCancelValidDomains = () => {
    setEditingValidDomains(false);
    setValidDomainsValue('');
    setValidDomainsError(null);
  };

  if (loading) {
    return (
      <div className="container mx-auto p-8">
        <Loading message="Loading organization details..." />
      </div>
    );
  }

  if (error || !lea) {
    return (
      <div className="container mx-auto p-8">
        <Alert color="failure">
          <span className="font-medium">Error!</span> {error || 'Organization not found'}
        </Alert>
        <Button
          color="gray"
          onClick={() => navigate('/leas')}
          className="mt-4"
        >
          <HiArrowLeft className="mr-2 h-4 w-4" />
          Back to Organizations
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-8">
      <PageHeader
        title={lea.nameOfInstitution}
        leading={<OrganizationLogo leaId={lea.leaId} name={lea.nameOfInstitution} logoUpdatedAt={lea.logoUpdatedAt} size="lg" />}
        breadcrumbs={[
          { label: 'Organizations', href: '/leas' },
          { label: lea.nameOfInstitution }
        ]}
        actions={
          <>
            <Button
              color="gray"
              size="sm"
              onClick={() => navigate('/leas')}
            >
              <HiArrowLeft className="mr-2 h-4 w-4" />
              Back
            </Button>
            {canEdit && (
              <Button
                color="light"
                size="sm"
                onClick={() => setIsEditMode(!isEditMode)}
                className={isEditMode
                  ? "text-gray-700 hover:text-gray-900 bg-gray-100 hover:bg-gray-200"
                  : "text-blue-600 hover:text-blue-700 bg-blue-50 hover:bg-blue-100"}
              >
                <HiPencil className="mr-2 h-4 w-4" />
                {isEditMode ? 'Done Editing' : 'Edit'}
              </Button>
            )}
          </>
        }
      />

      {/* Organization Overview Card */}
      <Card className="mb-6">
        <div className={`grid grid-cols-1 gap-6 ${(lea.geographicBoundary || (lea.districtsServed && lea.districtsServed.length > 0)) ? 'lg:grid-cols-[3fr_4fr_5fr]' : 'lg:grid-cols-2'}`}>
          {/* Left Column: Overview + Contact. A size container, so rows lay out by
              the column's width: it's narrow beside the map on wide screens. */}
          <div className="space-y-3 min-w-0 @container">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center">
              <HiOfficeBuilding className="mr-2 h-5 w-5 text-gray-400 dark:text-gray-500" />
              Overview
            </h2>

            {isEditMode && canEdit && (
              <OrganizationLogoEditor
                leaId={lea.leaId}
                name={lea.nameOfInstitution}
                logoUpdatedAt={lea.logoUpdatedAt}
                canSearchWebsite={isSuperAdmin}
                website={lea.webSite}
                onChanged={() => loadOrganization(lea.leaId, true)}
              />
            )}

            {/* Type and IDs */}
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                {lea.leaType && (
                  <Badge color="info">{lea.leaType}</Badge>
                )}
                {lea.operationalStatus && lea.operationalStatus !== 'Active' && (
                  <Badge color="gray">{lea.operationalStatus}</Badge>
                )}
              </div>
              <div className="flex items-center gap-2">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  AUN: {lea.identificationCodes?.find(code =>
                    code.systemType?.toLowerCase() === 'aun' ||
                    code.systemType?.toLowerCase() === 'state'
                  )?.identificationCode || 'N/A'}
                  {lea.identificationCodes?.find(code =>
                    code.systemType?.toLowerCase() === 'nces'
                  )?.identificationCode && (
                    <span>
                      {' '}• NCES: {lea.identificationCodes?.find(code =>
                        code.systemType?.toLowerCase() === 'nces'
                      )?.identificationCode}
                    </span>
                  )}
                </p>
                {isEditMode && isSuperAdmin && (
                  <button
                    type="button"
                    onClick={() => setShowIdCodeManager(!showIdCodeManager)}
                    aria-expanded={showIdCodeManager}
                    className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 underline"
                  >
                    {showIdCodeManager ? 'Hide' : 'Manage'}
                  </button>
                )}
              </div>

              {/* Identification Codes Management (superAdmin edit mode) */}
              {showIdCodeManager && isEditMode && isSuperAdmin && (
                <div className="mt-2 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg space-y-2">
                  <h4 className="text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Identification Codes</h4>
                  {idCodeError && (
                    <Alert color="failure" onDismiss={() => setIdCodeError(null)}>
                      <span className="text-xs">{idCodeError}</span>
                    </Alert>
                  )}
                  {/* Existing codes */}
                  {lea.identificationCodes?.map(code => (
                    <div key={code.educationOrganizationIdentificationCodeId} className="flex items-center justify-between text-sm bg-white dark:bg-gray-800 rounded px-2 py-1.5">
                      <div>
                        <span className="text-gray-500 dark:text-gray-400">{code.systemType || 'Unknown'}:</span>{' '}
                        <span className="font-medium text-gray-900 dark:text-white">{code.identificationCode}</span>
                      </div>
                      <div className="flex gap-1">
                        <IconButton
                          icon={HiPencil}
                          label={`Edit ${code.systemType || ''} code ${code.identificationCode}`.replace(/\s+/g, ' ')}
                          onClick={() => handleOpenIdCodeForm(code)}
                        />
                        <IconButton
                          icon={HiTrash}
                          tone="danger"
                          label={`Delete ${code.systemType || ''} code ${code.identificationCode}`.replace(/\s+/g, ' ')}
                          onClick={() => handleDeleteIdCode(code.educationOrganizationIdentificationCodeId)}
                        />
                      </div>
                    </div>
                  ))}
                  {/* Add/Edit form */}
                  {idCodeFormOpen && (
                    <form
                      className="space-y-2 pt-1"
                      onSubmit={e => { e.preventDefault(); handleSaveIdCode(); }}
                    >
                      <div className="grid grid-cols-1 gap-2 @sm:grid-cols-2">
                      <div>
                        <label htmlFor={`${fieldId}-code-type`} className={FIELD_LABEL_CLASS}>Code Type</label>
                        <select
                          id={`${fieldId}-code-type`}
                          value={idCodeForm.descriptorId}
                          onChange={e => setIdCodeForm(f => ({ ...f, descriptorId: parseInt(e.target.value) || 0 }))}
                          className={FIELD_CLASS}
                        >
                          <option value={0}>Select type...</option>
                          {idCodeDescriptors.map(d => (
                            <option key={d.descriptorId} value={d.descriptorId}>
                              {d.codeValue} – {d.shortDescription}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label htmlFor={`${fieldId}-code-value`} className={FIELD_LABEL_CLASS}>Code Value</label>
                        <input
                          id={`${fieldId}-code-value`}
                          type="text"
                          value={idCodeForm.identificationCode}
                          onChange={e => setIdCodeForm(f => ({ ...f, identificationCode: e.target.value }))}
                          placeholder="e.g. 102023003"
                          className={FIELD_CLASS}
                        />
                      </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          type="submit"
                          disabled={savingIdCode || !idCodeForm.identificationCode.trim()}
                          className={SAVE_BUTTON_CLASS}
                        >
                          {savingIdCode ? 'Saving...' : editingIdCode ? 'Update' : 'Add'}
                        </button>
                        <button
                          type="button"
                          onClick={() => { setIdCodeFormOpen(false); setEditingIdCode(null); }}
                          className={CANCEL_BUTTON_CLASS}
                        >
                          Cancel
                        </button>
                      </div>
                    </form>
                  )}
                  {!idCodeFormOpen && (
                    <button
                      onClick={() => handleOpenIdCodeForm()}
                      className="flex items-center gap-1 text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 mt-1"
                    >
                      <HiPlus className="h-3 w-3" />
                      Add Code
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Address */}
            {lea.addresses && lea.addresses.length > 0 ? (
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                  <HiLocationMarker className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </div>
                <div className="flex flex-1 min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                  <address className="min-w-0 grow basis-48 not-italic text-sm text-gray-900 dark:text-white">
                    <span className="block">
                      {lea.addresses[0].streetNumberName}
                      {lea.addresses[0].apartmentRoomSuiteNumber && `, ${lea.addresses[0].apartmentRoomSuiteNumber}`}
                    </span>
                    <span className="block text-gray-600 dark:text-gray-400">
                      {lea.addresses[0].city}, PA {lea.addresses[0].postalCode}
                    </span>
                  </address>
                  {isEditMode && (
                    <div className="flex gap-1">
                      <IconButton icon={HiPencil} label="Edit address" onClick={() => handleEditAddress(lea.addresses[0])} />
                      <IconButton icon={HiTrash} tone="danger" label="Delete address" onClick={() => handleDeleteAddress(lea.addresses[0].educationOrganizationAddressId)} />
                    </div>
                  )}
                </div>
              </div>
            ) : isEditMode ? (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                  <HiLocationMarker className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </div>
                <div className="flex-1">
                  <Button size="xs" color="light" onClick={handleAddAddress}>
                    <HiPlus className="mr-1 h-3 w-3" />
                    Add Address
                  </Button>
                </div>
              </div>
            ) : null}

            {/* Phone & Website: side by side when the column has room (28rem+) and the
                website isn't being edited; otherwise one per row */}
            <div className={`flex flex-col gap-3 ${editingWebsite ? '' : '@md:flex-row @md:gap-6'}`}>
              {/* Phone */}
              <div className="flex items-center gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                  <HiPhone className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </div>
                <div className="flex flex-1 min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
                  {lea.phones && lea.phones.length > 0 ? (
                    <a
                      href={`tel:${lea.phones[0].phoneNumber}`}
                      className="inline-flex items-center whitespace-nowrap text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium"
                    >
                      {lea.phones[0].phoneNumber}
                    </a>
                  ) : (
                    <span className="text-sm text-gray-500 dark:text-gray-400">No phone</span>
                  )}
                  {isEditMode && lea.phones && lea.phones.length > 0 && (
                    <span className="inline-flex gap-1">
                      <IconButton icon={HiPencil} label="Edit phone number" onClick={() => handleEditPhone(lea.phones[0])} />
                      <IconButton icon={HiTrash} tone="danger" label="Delete phone number" onClick={() => handleDeletePhone(lea.phones[0].educationOrganizationPhoneAssociationId)} />
                    </span>
                  )}
                  {isEditMode && (!lea.phones || lea.phones.length === 0) && (
                    <Button size="xs" color="light" onClick={handleAddPhone}>
                      <HiPlus className="mr-1 h-3 w-3" />
                      Add Phone
                    </Button>
                  )}
                </div>
              </div>

              {/* Website */}
              <div className={`flex gap-3 flex-1 min-w-0 ${editingWebsite ? 'items-start' : 'items-center'}`}>
                <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                  <HiGlobe className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  {editingWebsite ? (
                    <form
                      className="space-y-2"
                      onSubmit={(e) => { e.preventDefault(); handleSaveWebsite(); }}
                    >
                      <label htmlFor={`${fieldId}-website`} className={FIELD_LABEL_CLASS}>Website</label>
                      <input
                        id={`${fieldId}-website`}
                        type="text"
                        inputMode="url"
                        autoComplete="url"
                        spellCheck={false}
                        autoFocus
                        value={websiteValue}
                        onChange={(e) => setWebsiteValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Escape') handleCancelWebsite(); }}
                        placeholder="https://www.district.org"
                        aria-invalid={websiteError ? true : undefined}
                        aria-describedby={websiteError ? `${fieldId}-website-error` : undefined}
                        className={FIELD_CLASS}
                      />
                      {websiteError && (
                        <p id={`${fieldId}-website-error`} role="alert" className="text-sm text-red-700 dark:text-red-400">
                          {websiteError}
                        </p>
                      )}
                      <div className="flex gap-2">
                        <button type="submit" className={SAVE_BUTTON_CLASS}>Save</button>
                        <button type="button" onClick={handleCancelWebsite} className={CANCEL_BUTTON_CLASS}>Cancel</button>
                      </div>
                    </form>
                  ) : (
                    <>
                      {lea.webSite ? (
                        <a
                          href={lea.webSite.startsWith('http') ? lea.webSite : `https://${lea.webSite}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium wrap-anywhere"
                        >
                          {lea.webSite.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                        </a>
                      ) : (
                        <span className="text-sm text-gray-500 dark:text-gray-400">No website</span>
                      )}
                    </>
                  )}
                </div>
                {isEditMode && !editingWebsite && (
                  <IconButton icon={HiPencil} label="Edit website" onClick={handleEditWebsite} />
                )}
              </div>
            </div>

            {/* Email Domain */}
            {lea.emailDomain && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                  <HiMail className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-900 dark:text-white font-medium">@{lea.emailDomain}</p>
                </div>
              </div>
            )}

            {/* Organization Email Addresses */}
            {(lea.emails && lea.emails.length > 0 || isEditMode) && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-gray-100 dark:bg-gray-700 rounded-lg flex items-center justify-center flex-shrink-0">
                  <HiMail className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  {lea.emails && lea.emails.length > 0 ? (
                    <div className="space-y-2">
                      <p className="text-xs text-gray-500 dark:text-gray-400 uppercase font-medium">Organization Emails</p>
                      {/* Show first 2 emails, or all if showAllEmails is true or in edit mode */}
                      {(showAllEmails || isEditMode ? lea.emails : lea.emails.slice(0, 2)).map((email) => (
                        <div key={email.educationOrganizationEmailAddressId} className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <div className="min-w-0 grow basis-48">
                            <span className="text-xs text-gray-500 dark:text-gray-400">
                              {email.emailTypeName || 'General'}:
                            </span>{' '}
                            <a
                              href={`mailto:${email.emailAddress}`}
                              className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 font-medium wrap-anywhere"
                            >
                              {breakableEmail(email.emailAddress)}
                            </a>
                            {email.primaryEmailIndicator && (
                              <Badge color="info" size="xs" className="ml-2 inline-flex">Primary</Badge>
                            )}
                          </div>
                          {isEditMode && (
                            <span className="inline-flex gap-1">
                              <IconButton icon={HiPencil} label={`Edit email ${email.emailAddress}`} onClick={() => handleEditEmail(email)} />
                              <IconButton icon={HiTrash} tone="danger" label={`Delete email ${email.emailAddress}`} onClick={() => handleDeleteEmail(email.educationOrganizationEmailAddressId)} />
                            </span>
                          )}
                        </div>
                      ))}
                      {/* Show more toggle */}
                      {!isEditMode && lea.emails.length > 2 && (
                        <button
                          onClick={() => setShowAllEmails(!showAllEmails)}
                          className="text-xs text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300"
                        >
                          {showAllEmails ? 'Show less' : `Show ${lea.emails.length - 2} more`}
                        </button>
                      )}
                      {isEditMode && (
                        <Button size="xs" color="light" onClick={handleAddEmail}>
                          <HiPlus className="mr-1 h-3 w-3" />
                          Add Email
                        </Button>
                      )}
                    </div>
                  ) : isEditMode ? (
                    <Button size="xs" color="light" onClick={handleAddEmail}>
                      <HiPlus className="mr-1 h-3 w-3" />
                      Add Organization Email
                    </Button>
                  ) : null}
                </div>
              </div>
            )}

            {/* Valid Domains (superAdmin edit mode only) */}
            {isEditMode && isSuperAdmin && (
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900 rounded-lg flex items-center justify-center flex-shrink-0">
                  <HiGlobe className="w-4 h-4 text-amber-600 dark:text-amber-400" />
                </div>
                <div className="flex-1 min-w-0">
                  {editingValidDomains ? (
                    <form
                      className="space-y-2"
                      onSubmit={(e) => { e.preventDefault(); handleSaveValidDomains(); }}
                    >
                      <label htmlFor={`${fieldId}-domains`} className="block text-xs text-gray-600 dark:text-gray-400 uppercase font-medium">
                        Valid Login Domains
                      </label>
                      <input
                        id={`${fieldId}-domains`}
                        type="text"
                        autoComplete="off"
                        spellCheck={false}
                        autoFocus
                        value={validDomainsValue}
                        onChange={(e) => setValidDomainsValue(e.target.value)}
                        onKeyDown={(e) => { if (e.key === 'Escape') handleCancelValidDomains(); }}
                        placeholder="domain1.org, domain2.k12.pa.us"
                        aria-describedby={`${fieldId}-domains-hint${validDomainsError ? ` ${fieldId}-domains-error` : ''}`}
                        aria-invalid={validDomainsError ? true : undefined}
                        className={FIELD_CLASS}
                      />
                      <p id={`${fieldId}-domains-hint`} className="text-xs text-gray-600 dark:text-gray-400">
                        Comma-separated list of email domains that can log in under this organization.
                      </p>
                      {validDomainsError && (
                        <p id={`${fieldId}-domains-error`} role="alert" className="text-sm text-red-700 dark:text-red-400">
                          {validDomainsError}
                        </p>
                      )}
                      <div className="flex gap-2">
                        <button type="submit" className={SAVE_BUTTON_CLASS}>Save</button>
                        <button type="button" onClick={handleCancelValidDomains} className={CANCEL_BUTTON_CLASS}>Cancel</button>
                      </div>
                    </form>
                  ) : (
                    <>
                      <p className="text-xs text-gray-600 dark:text-gray-400 uppercase font-medium mb-1">Valid Login Domains</p>
                      <div className="flex items-center gap-2">
                        <p className="text-sm text-gray-900 dark:text-white font-medium wrap-anywhere">
                          {lea.validDomains || <span className="text-gray-500 dark:text-gray-400 italic font-normal">Not set</span>}
                        </p>
                        <IconButton icon={HiPencil} label="Edit valid login domains" onClick={handleEditValidDomains} />
                      </div>
                    </>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Middle Column: Leadership */}
          <div className={`space-y-4 lg:border-l lg:border-gray-200 dark:lg:border-gray-700 lg:pl-6 ${lea.geographicBoundary ? 'lg:border-r lg:pr-6' : ''}`}>
            <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Leadership</h3>

            {/* Superintendent */}
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900 rounded-full flex items-center justify-center flex-shrink-0">
                <HiUsers className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div className="flex-1 min-w-0">
                {superintendent ? (
                  <>
                    <Link
                      to={`/persons/${superintendent.personId}`}
                      className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline"
                    >
                      {superintendent.displayName}
                    </Link>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{superintendent.positionTitle || 'Superintendent'}</p>
                  </>
                ) : (
                  <>
                    <p className="text-sm text-gray-400 dark:text-gray-500 italic">Not assigned</p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">Superintendent</p>
                  </>
                )}
              </div>
            </div>

            {/* Assistant Superintendents */}
            {assistantSuperintendents.map(assistant => (
              <div key={assistant.personId} className="flex items-start gap-3">
                <div className="w-10 h-10 bg-blue-50 dark:bg-blue-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                  <HiUsers className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <Link
                    to={`/persons/${assistant.personId}`}
                    className="text-sm font-medium text-blue-600 dark:text-blue-400 hover:text-blue-700 dark:hover:text-blue-300 hover:underline"
                  >
                    {assistant.displayName}
                  </Link>
                  <p className="text-xs text-gray-500 dark:text-gray-400">
                    {assistant.positionTitle || 'Assistant Superintendent'}
                  </p>
                </div>
              </div>
            ))}

            {/* Counties */}
            {lea.counties && lea.counties.length > 0 && (
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 bg-gray-100 dark:bg-gray-700 rounded-full flex items-center justify-center flex-shrink-0">
                  <HiLocationMarker className="w-5 h-5 text-gray-500 dark:text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{lea.counties.join(', ')}</p>
                  <p className="text-xs text-gray-500 dark:text-gray-400">Counties Served</p>
                </div>
              </div>
            )}
          </div>

          {/* Right Column: District Map */}
          {lea.geographicBoundary ? (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">District Boundary</h3>
              <EmbeddedDistrictMap
                selectedOrgId={lea.educationOrganizationId}
                selectedLeaId={lea.leaId}
                height="275px"
                refreshKey={mapRefreshKey}
              />
            </div>
          ) : lea.districtsServed && lea.districtsServed.length > 0 ? (
            <div className="space-y-3">
              <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">Service Area</h3>
              <EmbeddedDistrictMap
                selectedOrgId={lea.educationOrganizationId}
                selectedLeaId={lea.leaId}
                height="275px"
                highlightedOrgIds={lea.districtsServed}
                schoolLeaIds={[lea.leaId]}
                refreshKey={mapRefreshKey}
              />
            </div>
          ) : null}
        </div>
      </Card>

      {/* Districts Served Card (CTCs/Jointures only) */}
      {((lea.districtsServed && lea.districtsServed.length > 0) ||
        (isEditMode && canEdit && (lea.leaType === 'CTC' || lea.leaType === 'Jointure'))) && (
        <Card className="mb-6">
          <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide mb-3">
            Districts Served
          </h3>
          {isEditMode && canEdit ? (
            <div className="space-y-3">
              <SearchableMultiSelect
                options={allOrgOptions}
                selectedIds={editingDistrictsServed}
                onChange={setEditingDistrictsServed}
                placeholder="Search and select districts..."
              />
              <div className="flex gap-2">
                <button
                  onClick={handleSaveDistrictsServed}
                  disabled={savingDistrictsServed}
                  className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded disabled:opacity-50"
                >
                  {savingDistrictsServed ? 'Saving...' : 'Save'}
                </button>
                <button
                  onClick={() => setEditingDistrictsServed(lea.districtsServed || [])}
                  disabled={savingDistrictsServed}
                  className="px-3 py-1.5 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {servedDistrictNames.length > 0 ? (
                servedDistrictNames.map(d => (
                  <Link
                    key={d.id}
                    to={`/leas/${d.leaId}`}
                    className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-blue-700 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/50 hover:bg-blue-100 dark:hover:bg-blue-900 rounded-lg transition-colors"
                  >
                    {d.name}
                  </Link>
                ))
              ) : (
                <p className="text-sm text-gray-400 dark:text-gray-500 italic">Loading districts...</p>
              )}
            </div>
          )}
        </Card>
      )}

      {/* Custom Tabs Implementation */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
        {/* Tab Headers */}
        <div className="border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-wrap items-center justify-between gap-4 px-6">
            <nav className="-mb-px flex space-x-8 overflow-x-auto" aria-label="Tabs">
            {[
              {
                id: 'schools',
                label: `Schools (${lea.schools?.length || 0})`,
                icon: HiAcademicCap
              },
              {
                id: 'staff',
                label: `Administrators (${lea.administrators?.filter(s => {
                  if (!showInactive) {
                    const isActive = !s.endDate || new Date(s.endDate) >= new Date();
                    if (!isActive) return false;
                  }
                  const role = s.role?.toLowerCase() || '';
                  const title = s.positionTitle?.toLowerCase() || '';
                  const isBoard = role === 'boardmember' || role.includes('board') ||
                                 (!s.role && (
                                   title.includes('board') || title.includes('president') ||
                                   title.includes('treasurer') || title.includes('secretary') ||
                                   title === 'members'
                                 ));
                  const isSolicitor = role === 'solicitor' || (!s.role && title.includes('solicitor'));
                  return !isBoard && !isSolicitor;
                }).length || 0})`,
                icon: HiUsers
              },
              {
                id: 'board',
                label: 'School Board',
                icon: HiUserGroup
              },
              // Conditionally add AIU Directory tab for AIU organization only
              ...(lea.nameOfInstitution === import.meta.env.VITE_AIU_ORGANIZATION_NAME ? [{
                id: 'aiu-directory',
                label: 'Full AIU Directory',
                icon: HiUserGroup
              }] : []),
              // Calendar tab — gated by the calendar.tabEnabled feature flag
              ...(calendarTabEnabled ? [{
                id: 'calendar',
                label: 'Calendar',
                icon: HiCalendar
              }] : []),
              // Activity tab — super admins only
              ...(isSuperAdmin ? [{
                id: 'activity',
                label: 'Activity',
                icon: HiChartBar
              }] : [])
            ].map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm
                    ${isActive
                      ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                      : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 hover:border-gray-300 dark:hover:border-gray-600'
                    }
                  `}
                >
                  <Icon className={`mr-2 h-5 w-5 ${isActive ? 'text-blue-600 dark:text-blue-400' : 'text-gray-400 dark:text-gray-500'}`} />
                  {tab.label}
                </button>
              );
            })}
            </nav>

            {/* Show Inactive Checkbox - SuperAdmin Only */}
            {isSuperAdmin && (
              <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400 cursor-pointer whitespace-nowrap py-4">
                <input
                  type="checkbox"
                  checked={showInactive}
                  onChange={(e) => setShowInactive(e.target.checked)}
                  className="rounded border-gray-300 dark:border-gray-600 text-blue-600 focus:ring-blue-500 dark:bg-gray-700"
                />
                Show inactive contacts
              </label>
            )}
          </div>
        </div>

        {/* Tab Content */}
        <div className="p-6">
          {activeTab === 'schools' && (
            <SchoolsTab
              lea={lea}
              onUpdate={() => loadOrganization(lea.leaId, true)}
              canEdit={canEdit}
              canAddSchool={canAddSchools}
              canDeleteSchool={isSuperAdmin}
              showClosedSchools={showClosedSchools}
              onToggleShowClosedSchools={setShowClosedSchools}
            />
          )}
          {activeTab === 'staff' && (
            <StaffTab
              lea={lea}
              onUpdate={() => loadOrganization(lea.leaId, true)}
              canEdit={canEdit}
            />
          )}
          {activeTab === 'board' && (
            <BoardTab
              lea={lea}
              onUpdate={() => loadOrganization(lea.leaId, true)}
              canEdit={canEdit}
              canViewAddresses={isSuperAdmin || canEdit}
            />
          )}
          {activeTab === 'aiu-directory' && lea.nameOfInstitution === import.meta.env.VITE_AIU_ORGANIZATION_NAME && (
            <AIUDirectoryTab />
          )}
          {activeTab === 'calendar' && calendarTabEnabled && (
            <CalendarTab lea={lea} canEdit={canEdit} />
          )}
          {activeTab === 'activity' && isSuperAdmin && (
            <ActivityTab lea={lea} />
          )}
        </div>
      </div>

      {/* Edit Modals */}
      <EditAddressModal
        isOpen={addressModalOpen}
        onClose={() => setAddressModalOpen(false)}
        onSave={handleSaveAddress}
        address={editingAddress}
        title={editingAddress ? 'Edit Address' : 'Add Address'}
      />

      <EditPhoneModal
        isOpen={phoneModalOpen}
        onClose={() => setPhoneModalOpen(false)}
        onSave={handleSavePhone}
        phone={editingPhone}
        title={editingPhone ? 'Edit Phone' : 'Add Phone'}
      />

      <EditOrgEmailModal
        isOpen={emailModalOpen}
        onClose={() => setEmailModalOpen(false)}
        onSave={handleSaveEmail}
        email={editingEmail}
        title={editingEmail ? 'Edit Organization Email' : 'Add Organization Email'}
      />
    </div>
  );
};